import { message } from "antd";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../hooks/redux-hooks";
import { getEHRSyncRun, startEHRSync } from "../../service/ehr.service";
import { refreshClientsAfterSync, setRecentlySyncedIds } from "../../slices/clientSlice";
import type { EHRSyncRun } from "../../types/index.type";
import { FAILURE_COPY } from "./copy";

export const SYNC_POLL_INTERVAL_MS = 2500;
// Hard ceiling on a poll loop, applied even when the run blob carries no usable
// deadline. Matches the server's own 300s budget for a click.
export const SYNC_MAX_POLL_MS = 300_000;
// How long a finished run's result line stays on screen before the status strip
// collapses. Failures leave through a toast, so this only governs the success line.
export const SYNC_RESULT_LINGER_MS = 8_000;

// "cooldown" is not a run state: it is the per-user throttle the POST reports
// with a 429, and it outlives whichever run earned it.
export type EhrSyncPhase = "idle" | "running" | "ok" | "busy" | "refused" | "error" | "cooldown";

const SETTLED_PHASES: EhrSyncPhase[] = ["ok", "busy", "refused", "error"];

export type UseEhrSync = {
  phase: EhrSyncPhase;
  run: EHRSyncRun | null;
  /** Epoch ms of the last run that finished ok, or null if none has this session. */
  lastSyncedAt: number | null;
  cooldownRemainingS: number;
  /**
   * True once the gate has refused this user. Eligibility is computed at bootstrap,
   * so a flag change mid-session is only discovered on the click — after which the
   * control withdraws instead of inviting a second refusal.
   */
  hidden: boolean;
  startSync: () => void;
};

// The remaining throttle in seconds, from the 429 the start endpoint answers with.
const cooldownSecondsFrom = (error: unknown): number | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null;
  const body = error.response.data as { retry_after?: number } | undefined;
  const seconds = Number(body?.retry_after);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 1;
};

// The two statuses the start endpoint's gate refuses with. Eligibility is computed
// at bootstrap, so it can go stale in a session that outlives a flag change. 401 is
// deliberately not one of them — that is a dead session, and the axios interceptor
// already handles it.
const isRefusal = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  return error.response?.status === 400 || error.response?.status === 403;
};

export const useEhrSync = (): UseEhrSync => {
  const dispatch = useAppDispatch();
  const [phase, setPhase] = useState<EhrSyncPhase>("idle");
  const [run, setRun] = useState<EHRSyncRun | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownRemainingS, setCooldownRemainingS] = useState(0);
  const [hidden, setHidden] = useState(false);

  // True from the click until the run settles. Read and written synchronously
  // because React flushes `phase` too late to stop a second click in the same
  // tick — the endpoint's idempotency is the backstop, not this.
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!runId) return;

    const startedAt = Date.now();
    // The deadline is used as a SPAN, not as an absolute instant: a browser clock
    // minutes off the server's would otherwise expire the run at once, or never.
    let budgetMs = SYNC_MAX_POLL_MS;
    let cancelled = false;
    let polling = false;

    const settle = (next: EhrSyncPhase) => {
      cancelled = true;
      inFlightRef.current = false;
      setPhase(next);
      setRunId(null);
    };

    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const { run: latest } = (await getEHRSyncRun(runId)).data;
        if (cancelled) return;
        setRun(latest);
        if (latest.started_ts != null && latest.deadline_ts != null) {
          budgetMs = Math.min((latest.deadline_ts - latest.started_ts) * 1000, SYNC_MAX_POLL_MS);
        }
        if (latest.status !== "running") {
          if (latest.status === "ok") {
            setLastSyncedAt(latest.finished_ts ? latest.finished_ts * 1000 : Date.now());
            dispatch(refreshClientsAfterSync());
          }
          settle(latest.status);
          return;
        }
      } catch (error) {
        // A dropped poll is not a dropped run; the deadline below is what ends it.
        // 401 means the session went away — the axios interceptor redirects, and
        // logging every tick would only flood Sentry.
        if (!axios.isAxiosError(error) || error.response?.status !== 401) {
          console.error("Error polling EHR sync run:", error);
        }
      } finally {
        polling = false;
      }
      if (!cancelled && Date.now() - startedAt >= budgetMs) settle("error");
    };

    poll();
    const timer = setInterval(poll, SYNC_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [runId, dispatch]);

  // Failures leave through the app's toast channel rather than parked text: the
  // sidebar is a list, not a console. Refusal also withdraws the control.
  useEffect(() => {
    if (phase === "busy") message.warning(FAILURE_COPY.busy);
    else if (phase === "error") message.error(FAILURE_COPY.error);
    else if (phase === "refused") {
      message.error(FAILURE_COPY.refused);
      setHidden(true);
    }
    if (!SETTLED_PHASES.includes(phase)) return;
    const timer = setTimeout(() => setPhase("idle"), SYNC_RESULT_LINGER_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (cooldownUntil == null) return;

    const tick = () => {
      const remaining = Math.ceil((cooldownUntil - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldownRemainingS(remaining);
        return;
      }
      setCooldownRemainingS(0);
      setCooldownUntil(null);
      setPhase("idle");
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  const startSync = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPhase("running");
    setRun(null);

    try {
      const { data } = await startEHRSync();
      // Only a run that actually started retires the previous run's "new" markers;
      // a click the throttle or the gate turned away leaves them for the clinician.
      dispatch(setRecentlySyncedIds([]));
      setRunId(data.run_id);
    } catch (error) {
      inFlightRef.current = false;
      const seconds = cooldownSecondsFrom(error);
      if (seconds != null) {
        setCooldownUntil(Date.now() + seconds * 1000);
        setPhase("cooldown");
        return;
      }
      if (isRefusal(error)) {
        setPhase("refused");
        return;
      }
      console.error("Error starting EHR sync:", error);
      setPhase("error");
    }
  }, [dispatch]);

  return { phase, run, lastSyncedAt, cooldownRemainingS, hidden, startSync };
};

export default useEhrSync;
